export const REQUEST_STATUSES = ['ACTIVE', 'SUBMITTED', 'EXPIRED', 'DISABLED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const REVIEW_STATUSES = ['NEW', 'REVIEWED', 'ARCHIVED'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const CONSENT_VALUES = ['NAMED', 'ANONYMOUS', 'PRIVATE'] as const;
export type Consent = (typeof CONSENT_VALUES)[number];

export const TERNARY_ANSWERS = ['YES', 'MAYBE', 'NO'] as const;
export type TernaryAnswer = (typeof TERNARY_ANSWERS)[number];

export const EXPIRATION_DAY_OPTIONS = [30, 60, 90] as const;
export type ExpirationDays = (typeof EXPIRATION_DAY_OPTIONS)[number];

export const DEFAULT_EXPIRATION_DAYS = 90;
export const DEFAULT_RESPONSE_RETENTION_MONTHS = 24;

export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type PhotoContentType = (typeof PHOTO_CONTENT_TYPES)[number];

export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
export const MAX_COMMENT_CHARS = 1000;
export const MAX_DISPLAY_NAME_CHARS = 80;
export const MAX_ADMIN_NOTES_CHARS = 2000;
export const ADMIN_GROUP_NAME = 'Administrators';
export const VIEWER_GROUP_NAME = 'Viewers';

export const INVOICE_STATUSES = [
  'DRAFT',
  'ISSUED',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'VOID',
  'CREDITED',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_SOURCE_TYPES = ['SHOPIFY', 'MANUAL'] as const;
export type InvoiceSourceType = (typeof INVOICE_SOURCE_TYPES)[number];

export const DEFAULT_INVOICE_CURRENCY = 'USD';
export const DEFAULT_INVOICE_TERMS = 'Due on receipt';
export const INVOICE_CALCULATION_VERSION = 1;
export const INVOICE_TEMPLATE_VERSION = 1;
export const INVOICE_NUMBER_PREFIX = 'FOIS';
export const DEFAULT_INVOICE_TIMEZONE = 'America/Los_Angeles';
export const DEFAULT_BUSINESS_LEGAL_NAME = 'FESTIVALSOFINDIASTORE';
export const DEFAULT_BUSINESS_WEBSITE = 'festivalsofindiastore.com';
export const INVOICE_THANK_YOU =
  'Thank you for supporting FESTIVALSOFINDIASTORE. Your purchase helps us share authentic Indian traditions and celebration essentials with our community.';
export const INVOICE_BRAND_STORY_HEADING = 'Our brand story and vision';
export const INVOICE_BRAND_STORY =
  "FESTIVALSOFINDIASTORE brings authentic Indian festival décor, pooja essentials, and handcrafted treasures from India's artisan communities to homes across America.";
export const INVOICE_BRAND_TAGLINE =
  'Sourced in India · Shipped from California · Supporting artisan communities';
export const INVOICE_PAYMENT_INSTRUCTIONS =
  'Payment is due according to the terms on this invoice. FOIS accepts Zelle and cash for manual invoices.';
export const MAX_INVOICE_LINE_ITEMS = 50;

export const INVOICE_GENERATION_STATUSES = ['GENERATING', 'READY', 'FAILED'] as const;
export type InvoiceGenerationStatus = (typeof INVOICE_GENERATION_STATUSES)[number];

export const INVOICE_AUDIT_EVENT_TYPES = [
  'DRAFT_CREATED',
  'DRAFT_UPDATED',
  'INVOICE_ISSUED',
  'PDF_GENERATED',
  'PAYMENT_RECORDED',
  'PAYMENT_VOIDED',
  'PAYMENT_REFUNDED',
  'INVOICE_VOIDED',
  'CREDIT_NOTE_ISSUED',
  'FEEDBACK_LINK_CREATED',
  'FEEDBACK_LINK_FAILED',
  'INVOICE_SHARED',
  'INVOICE_EMAILED',
] as const;
export type InvoiceAuditEventType = (typeof INVOICE_AUDIT_EVENT_TYPES)[number];

export const PAYMENT_METHODS = ['ZELLE', 'CASH', 'CARD', 'SHOPIFY', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ['RECORDED', 'VOIDED', 'REFUNDED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const INVOICE_FEEDBACK_LINK_STATUSES = ['READY', 'FAILED', 'SKIPPED'] as const;
export type InvoiceFeedbackLinkStatus = (typeof INVOICE_FEEDBACK_LINK_STATUSES)[number];

export const DEFAULT_INVOICE_SHARE_EXPIRATION_DAYS = 30;
export const INVOICE_FEEDBACK_INVITE =
  'Share your experience with FOIS UtsavVoice. Your honest feedback helps us improve.';
export const STORY_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'IN_REVIEW',
  'APPROVED_PRIVATE',
  'APPROVED_FOR_PUBLICATION',
  'REJECTED',
  'ARCHIVED',
] as const;
export type StoryStatus = (typeof STORY_STATUSES)[number];

export const STORY_AUDIT_EVENT_TYPES = [
  'STORY_CREATED',
  'STORY_OPENED',
  'DRAFT_SAVED',
  'STORY_SUBMITTED',
  'CONSENT_RECORDED',
  'REVIEW_STARTED',
  'STORY_APPROVED',
  'STORY_REJECTED',
  'STORY_ARCHIVED',
  'UPLOAD_INITIATED',
  'UPLOAD_COMPLETED',
  'UPLOAD_ABORTED',
  'VALIDATION_PASSED',
  'VALIDATION_FAILED',
  'DERIVATIVE_GENERATED',
  'MEDIA_APPROVED',
  'MEDIA_REJECTED',
] as const;
export type StoryAuditEventType = (typeof STORY_AUDIT_EVENT_TYPES)[number];

export const DEFAULT_PUBLIC_STORY_TOKEN_DAYS = 90;
export const DEFAULT_STORY_CONSENT_VERSION = 'fois-stories-consent-v1';
export const MAX_STORY_TESTIMONIAL_CHARS = 1500;
export const MAX_STORY_CELEBRATION_CHARS = 750;
export const MAX_STORY_LOCATION_CHARS = 100;
export const STORY_WELCOME =
  'We’d love to see how FOIS became part of your celebration. Share photos, short videos, or a few words about your experience. You decide whether your story may be featured publicly.';
export const STORY_RIGHTS_CONFIRMATION =
  'I own or have permission to share this content. I have permission from recognizable adults shown in it. If a minor is recognizable, I am their parent or legal guardian or have permission from their parent or legal guardian.';
export const STORY_CONFIRMATION =
  'Thank you for sharing your celebration with FESTIVALSOFINDIASTORE. Your submission will remain private while our team reviews it.';

export const STORY_MEDIA_TYPES = ['PHOTO', 'VIDEO'] as const;
export type StoryMediaType = (typeof STORY_MEDIA_TYPES)[number];

export const STORY_MEDIA_STATUSES = [
  'CREATED',
  'UPLOADING',
  'UPLOADED',
  'VALIDATING',
  'PROCESSING',
  'READY',
  'FAILED_RETRYABLE',
  'REJECTED_AUTOMATED',
  'REMOVED_BY_CUSTOMER',
  'APPROVED',
  'REJECTED_BY_ADMIN',
  'ARCHIVED',
  'DELETED',
] as const;
export type StoryMediaStatus = (typeof STORY_MEDIA_STATUSES)[number];

export const STORY_PHOTO_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export type StoryPhotoContentType = (typeof STORY_PHOTO_CONTENT_TYPES)[number];

export const STORY_VIDEO_CONTENT_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
export type StoryVideoContentType = (typeof STORY_VIDEO_CONTENT_TYPES)[number];

export const DEFAULT_STORY_MAX_PHOTOS = 5;
export const DEFAULT_STORY_MAX_VIDEOS = 5;
export const DEFAULT_STORY_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const DEFAULT_STORY_MAX_PHOTO_BYTES = 50 * 1024 * 1024;
export const DEFAULT_STORY_VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const DEFAULT_STORY_MAX_VIDEO_BYTES = 500 * 1024 * 1024;
export const DEFAULT_STORY_VIDEO_MAX_SECONDS = 60;
export const STORY_PHOTO_UPLOAD_TTL_SECONDS = 300;
export const STORY_VIDEO_UPLOAD_TTL_SECONDS = 3600;
export const STORY_MEDIA_READ_TTL_SECONDS = 300;
export const STORY_MULTIPART_PART_SIZE_BYTES = 8 * 1024 * 1024;
export const STORY_MULTIPART_MAX_PARTS_PER_REQUEST = 10;

export const CREDIT_NOTE_NUMBER_PREFIX = 'FOIS-CN';
export const MAX_INVOICE_DESCRIPTION_CHARS = 200;
export const MAX_INVOICE_NOTE_CHARS = 1000;
export const MAX_PAYMENT_NOTE_CHARS = 500;
export const MAX_VOID_REASON_CHARS = 500;
